import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import TextField from "@/components/form/textField/TextField"
import { SummarySettings } from "@/types/report"
import AddComponentButton from "@/app/report/components/setting/AddComponentButton"
import SettingSectionItem from "@/app/report/components/setting/SettingSectionItem"
import MetricSetting from "@/app/report/components/setting/MetricSetting"
import RadioGroup from "@/components/form/radioGroup/RadioGroup"
import { SettingSectionProps } from "@/app/report/common/type"
import DeleteItemDialog from "@/app/report/components/setting/DeleteItemDialog"
import { SECTION_TYPE } from "@/app/report/common/constants"

const DEFAULT_SUMMARY_ITEMS = [
  {
    title: "Volume",
    metric: "totalDistance",
    calcType: "average",
  },
  {
    title: "Intensity",
    metric: "totalDistancePerMinute",
    calcType: "average",
  },
  {
    title: "Speed",
    metric: "maxSpeed",
    calcType: "average",
  },
  {
    title: "Agility",
    metric: "accelEffort1Count",
    calcType: "average",
  },
  {
    title: "Power",
    metric: "powerCount",
    calcType: "average",
  },
  {
    title: "Response",
    metric: "rpe",
    calcType: "average",
  },
]

export default function SummarySection({
  settings,
  tempTemplate,
  setTempTemplate,
  sectionIndex,
}: SettingSectionProps<SummarySettings[]> & { sectionIndex: number }) {
  const { t } = useTranslation()
  const [tempSummaryItems, setTempSummaryItems] = useState<SummarySettings[]>(DEFAULT_SUMMARY_ITEMS)
  const [deleteItemIndex, setDeleteItemIndex] = useState<number | null>(null)

  useEffect(() => {
    setTempSummaryItems(settings)
  }, [settings])

  useEffect(() => {
    setTempTemplate((prev) => {
      if (!prev) return null
      const updatedContent = [...prev.content]
      updatedContent[sectionIndex] = { ...updatedContent[sectionIndex], settings: tempSummaryItems }
      return { ...prev, content: updatedContent }
    })
  }, [tempSummaryItems, sectionIndex])

  return (
    <>
      <div className="DailySettingSummarySectionCont">
        {tempSummaryItems.map((setting, index) => {
          return (
            <SettingSectionItem
              key={`summary-item-${index}`}
              onDeleteItem={index > 2 ? () => setDeleteItemIndex(index) : undefined}>
              <div className="DailySettingSummaryItemCont">
                <TextField
                  label={t("title")}
                  value={setting.title}
                  onChange={(e) => {
                    const newValue = e.target.value

                    setTempSummaryItems((prev) => {
                      const updated = [...prev]
                      updated[index] = { ...updated[index], title: newValue }

                      return updated
                    })
                  }}
                  className="daily-setting-summary-title"
                  error={setting.title.length < 1}
                  showMessage={setting.title.length < 1}
                  message={t("rules.required")}
                />
                <MetricSetting
                  selectedMetric={setting.metric}
                  setSelectedMetric={(value) =>
                    setTempSummaryItems((prev) => {
                      const updated = [...prev]
                      updated[index] = { ...updated[index], metric: value }

                      return updated
                    })
                  }
                  className="daily-setting-summary-metric"
                />
                <div className="daily-setting-summary-radio">
                  <p>{t("metricCalculationOption")}</p>
                  <RadioGroup
                    direction="row"
                    items={[
                      { label: t("average"), value: "average" },
                      { label: t("total"), value: "total" },
                    ]}
                    value={setting.calcType}
                    onChange={(_, newVal) => {
                      if (!newVal) {
                        return
                      }
                      setTempSummaryItems((prev) => {
                        const updated = [...prev]
                        updated[index] = { ...updated[index], calcType: newVal }

                        return updated
                      })
                    }}
                  />
                </div>
              </div>
            </SettingSectionItem>
          )
        })}
        {tempSummaryItems.length < 6 && (
          <AddComponentButton
            onClick={() => {
              setTempSummaryItems((prev) => [
                ...prev,
                {
                  title: "",
                  metric: "",
                  calcType: "average",
                },
              ])
            }}
          />
        )}
      </div>

      <DeleteItemDialog
        deleteItemIndex={deleteItemIndex}
        setDeleteItemIndex={setDeleteItemIndex}
        onDeleteAction={(index) =>
          setTempSummaryItems((prev) => prev.filter((_, i) => i !== index))
        }
      />
    </>
  )
}
